import { useId, useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Reference FAQ list: full-width rows between hairlines, the question in the
 * sub-display face and a "+" that turns into a cross when open. Opening one
 * item closes the others; the panel opens by animating grid-template-rows
 * from 0fr to 1fr (the reference's mechanic), so it needs no measuring.
 * Closed panels are inert: out of the tab order and the accessibility tree.
 * The global reduced-motion rule collapses the transition.
 */
export function FaqAccordion({
  items,
  className,
}: {
  items: { question: string; answer: string }[]
  className?: string
}) {
  const [open, setOpen] = useState<number | null>(null)
  const baseId = useId()

  return (
    <div className={cn('border-t border-[var(--color-landing-gray-900)] dark:border-white/70', className)}>
      {items.map((item, index) => {
        const isOpen = open === index
        const triggerId = `${baseId}-q${index}`
        const panelId = `${baseId}-a${index}`
        return (
          <div
            key={item.question}
            className="border-b border-[var(--color-landing-gray-200)] dark:border-[var(--color-landing-gray-800)]"
          >
            <h3>
              <button
                type="button"
                id={triggerId}
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpen(isOpen ? null : index)}
                className="group flex w-full items-start justify-between gap-6 py-6 text-left font-[family-name:var(--font-landing-subdisplay)] text-lg font-semibold leading-snug tracking-[-0.01em] text-[var(--color-landing-gray-900)] transition-colors duration-200 hover:text-[var(--color-landing-accent)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-landing-accent)] dark:text-white dark:hover:text-[#8b85ff] lg:text-xl"
              >
                <span>{item.question}</span>
                <span
                  aria-hidden
                  className={cn(
                    'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center border transition-[background-color,border-color,color] duration-200',
                    isOpen
                      ? 'border-[var(--color-landing-accent)] bg-[var(--color-landing-accent)] text-white'
                      : 'border-current'
                  )}
                >
                  <Plus
                    className={cn(
                      'h-4 w-4 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
                      isOpen && 'rotate-45'
                    )}
                    strokeWidth={2}
                  />
                </span>
              </button>
            </h3>
            <div
              id={panelId}
              role="region"
              aria-labelledby={triggerId}
              inert={!isOpen}
              className={cn(
                'grid transition-[grid-template-rows] duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
                isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
              )}
            >
              <div className="min-h-0 overflow-hidden">
                <p className="max-w-[62ch] pb-7 pr-12 landing-text-body text-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]">
                  {item.answer}
                </p>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
